import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getApiUrl } from '../../utils/api';
import { toast } from 'sonner';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import {
  Save, ArrowLeft, Upload, X, Plus, GripVertical,
  ChevronDown, ChevronUp, Eye, Globe,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';

const API_URL = getApiUrl();

const CATEGORIES = [
  { value: 'trimester_1', label: 'Trimester 1' },
  { value: 'trimester_2', label: 'Trimester 2' },
  { value: 'trimester_3', label: 'Trimester 3' },
  { value: 'birth_preparation', label: 'Birth Preparation' },
  { value: 'garbh_sanskar', label: 'Garbh Sanskar' },
  { value: 'breathing_meditation', label: 'Breathing & Meditation' },
  { value: 'postpartum', label: 'Postpartum Recovery' },
  { value: 'expert_insights', label: 'Expert Insights' },
];

const TRIMESTER_OPTIONS = ['T1', 'T2', 'T3'];
const BADGE_OPTIONS = [
  { value: '', label: 'None' },
  { value: 'Key Guide', label: 'Key Guide' },
  { value: 'Must Read', label: 'Must Read' },
];

const QUILL_MODULES = {
  toolbar: [
    [{ header: [2, 3, 4, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['blockquote', 'code-block'],
    ['link', 'image'],
    [{ align: [] }],
    ['clean'],
  ],
};

const STATUS_COLORS = {
  published: 'bg-green-100 text-green-700',
  draft: 'bg-yellow-100 text-yellow-700',
  archived: 'bg-slate-100 text-slate-500',
};

export default function BlogPostEditor() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const isNew = slug === 'new';
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [authors, setAuthors] = useState([]);
  const [publishedSlugs, setPublishedSlugs] = useState([]);
  const [seoOpen, setSeoOpen] = useState(false);

  const [form, setForm] = useState({
    title: '',
    excerpt: '',
    meta_description: '',
    content: '',
    cover_image_url: '',
    cover_image_alt: '',
    category: 'expert_insights',
    trimester_tags: [],
    author_slug: '',
    badge: '',
    readers_count: 0,
    key_takeaways: [''],
    faqs: [{ q: '', a: '' }],
    next_reads: [],
    publish_date: new Date().toISOString().split('T')[0],
    status: 'draft',
    slug: '',
    is_featured: false,
  });

  const fetchPost = useCallback(async () => {
    if (isNew) return;
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_URL}/api/admin/blog/posts/${slug}`);
      setForm({
        title: data.title || '',
        excerpt: data.excerpt || '',
        meta_description: data.meta_description || '',
        content: data.content || '',
        cover_image_url: data.cover_image_url || '',
        cover_image_alt: data.cover_image_alt || '',
        category: data.category || 'expert_insights',
        trimester_tags: data.trimester_tags || [],
        author_slug: data.author_slug || '',
        badge: data.badge || '',
        readers_count: data.readers_count || 0,
        key_takeaways: data.key_takeaways?.length ? data.key_takeaways : [''],
        faqs: data.faqs?.length ? data.faqs : [{ q: '', a: '' }],
        next_reads: data.next_reads || [],
        publish_date: data.publish_date ? data.publish_date.split('T')[0] : '',
        status: data.status || 'draft',
        slug: data.slug || '',
        is_featured: data.is_featured || false,
      });
    } catch (err) {
      toast.error('Failed to load post');
      navigate('/blog/posts');
    } finally {
      setLoading(false);
    }
  }, [slug, isNew, navigate]);

  const fetchAuthors = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/blog/authors`);
      setAuthors((data.authors || []).filter(a => a.is_active));
    } catch (err) { /* silent */ }
  }, []);

  const fetchPublishedSlugs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/blog/posts?status=published`);
      setPublishedSlugs((data.posts || []).map(p => p.slug).filter(s => s !== slug));
    } catch (err) { /* silent */ }
  }, [slug]);

  useEffect(() => {
    fetchPost();
    fetchAuthors();
    fetchPublishedSlugs();
  }, [fetchPost, fetchAuthors, fetchPublishedSlugs]);

  const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  // ─── Save ─────────────────────────
  const handleSave = async (andPublish = false) => {
    if (!form.title.trim()) {
      toast.error('Title is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        badge: form.badge || null,
        key_takeaways: form.key_takeaways.filter(k => k.trim()),
        faqs: form.faqs.filter(f => f.q.trim() && f.a.trim()),
        next_reads: form.next_reads,
      };

      let savedSlug;
      if (isNew) {
        const { data } = await axios.post(`${API_URL}/api/admin/blog/posts`, payload);
        savedSlug = data.slug;
        toast.success('Post created');
      } else {
        const { data } = await axios.put(`${API_URL}/api/admin/blog/posts/${slug}`, payload);
        savedSlug = data.slug;
        toast.success('Post saved');
      }

      if (andPublish && savedSlug) {
        try {
          await axios.patch(`${API_URL}/api/admin/blog/posts/${savedSlug}/status`, { status: 'published' });
          toast.success('Post published');
        } catch (pubErr) {
          toast.error(pubErr.response?.data?.detail || 'Cannot publish');
        }
      }

      navigate(`/blog/posts/${savedSlug}`);
      if (!isNew && savedSlug !== slug) {
        // Slug changed, reload
        window.location.reload();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (isNew) return;
    try {
      await axios.patch(`${API_URL}/api/admin/blog/posts/${slug}/status`, { status: newStatus });
      updateField('status', newStatus);
      toast.success(`Status changed to ${newStatus}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Status change failed');
    }
  };

  // ─── Image upload ─────────────────
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/blog/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      updateField('cover_image_url', data.url);
      toast.success('Image uploaded');
    } catch (err) {
      toast.error('Upload failed');
    }
  };

  // ─── Key Takeaways ─────────────────
  const addTakeaway = () => {
    if (form.key_takeaways.length >= 5) return;
    updateField('key_takeaways', [...form.key_takeaways, '']);
  };
  const updateTakeaway = (idx, val) => {
    const updated = [...form.key_takeaways];
    updated[idx] = val;
    updateField('key_takeaways', updated);
  };
  const removeTakeaway = (idx) => {
    updateField('key_takeaways', form.key_takeaways.filter((_, i) => i !== idx));
  };

  // ─── FAQs ─────────────────────────
  const addFaq = () => {
    if (form.faqs.length >= 6) return;
    updateField('faqs', [...form.faqs, { q: '', a: '' }]);
  };
  const updateFaq = (idx, field, val) => {
    const updated = [...form.faqs];
    updated[idx] = { ...updated[idx], [field]: val };
    updateField('faqs', updated);
  };
  const removeFaq = (idx) => {
    updateField('faqs', form.faqs.filter((_, i) => i !== idx));
  };

  // ─── Trimester tags ───────────────
  const toggleTrimesterTag = (tag) => {
    const tags = form.trimester_tags.includes(tag)
      ? form.trimester_tags.filter(t => t !== tag)
      : [...form.trimester_tags, tag];
    updateField('trimester_tags', tags);
  };

  // ─── Next reads ───────────────────
  const toggleNextRead = (s) => {
    if (form.next_reads.includes(s)) {
      updateField('next_reads', form.next_reads.filter(r => r !== s));
    } else if (form.next_reads.length < 3) {
      updateField('next_reads', [...form.next_reads, s]);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="blog-post-editor">
      {/* Top Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/blog/posts')} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="back-to-posts-btn">
            <ArrowLeft className="w-5 h-5 text-slate-500" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-slate-900" data-testid="post-editor-title">
              {isNew ? 'New Post' : 'Edit Post'}
            </h1>
            {!isNew && (
              <div className="flex items-center gap-2 mt-1">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[form.status]}`}>
                  {form.status}
                </span>
                {form.is_featured && (
                  <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-xs">Featured</Badge>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isNew && form.status === 'draft' && (
            <Button variant="outline" onClick={() => handleStatusChange('published')} data-testid="publish-btn">
              Publish
            </Button>
          )}
          {!isNew && form.status === 'published' && (
            <Button variant="outline" onClick={() => handleStatusChange('archived')} className="text-slate-600" data-testid="archive-btn">
              Archive
            </Button>
          )}
          {!isNew && form.status === 'archived' && (
            <Button variant="outline" onClick={() => handleStatusChange('draft')} data-testid="revert-draft-btn">
              Revert to Draft
            </Button>
          )}
          <Button onClick={() => handleSave(false)} disabled={saving} className="gap-2" data-testid="save-post-btn">
            <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save'}
          </Button>
          {isNew && (
            <Button onClick={() => handleSave(true)} disabled={saving} variant="default" className="gap-2 bg-green-600 hover:bg-green-700" data-testid="save-publish-btn">
              Save & Publish
            </Button>
          )}
        </div>
      </div>

      {/* Main Layout: Content + Sidebar */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Title */}
          <div className="bg-white rounded-lg border border-slate-200 p-6">
            <Label className="text-sm font-medium">Title</Label>
            <Input
              value={form.title}
              onChange={(e) => updateField('title', e.target.value)}
              placeholder="Complete Guide to Prenatal Yoga..."
              className="text-lg font-semibold mt-1"
              data-testid="post-title-input"
            />
          </div>

          {/* Excerpt */}
          <div className="bg-white rounded-lg border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-1">
              <Label className="text-sm font-medium">Excerpt</Label>
              <span className={`text-xs ${(form.excerpt?.length || 0) > 120 ? 'text-red-500' : 'text-slate-400'}`}>
                {form.excerpt?.length || 0}/120
              </span>
            </div>
            <Input
              value={form.excerpt}
              onChange={(e) => updateField('excerpt', e.target.value)}
              placeholder="One-line emotional hook for cards..."
              maxLength={120}
              data-testid="post-excerpt-input"
            />
          </div>

          {/* Rich Text Editor */}
          <div className="bg-white rounded-lg border border-slate-200 p-6">
            <Label className="text-sm font-medium mb-2 block">Content</Label>
            <div className="prose-editor" data-testid="post-content-editor">
              <ReactQuill
                theme="snow"
                value={form.content}
                onChange={(val) => updateField('content', val)}
                modules={QUILL_MODULES}
                placeholder="Write your article..."
                style={{ minHeight: '300px' }}
              />
            </div>
          </div>

          {/* Cover Image */}
          <div className="bg-white rounded-lg border border-slate-200 p-6">
            <Label className="text-sm font-medium mb-3 block">Cover Image</Label>
            <div className="flex flex-col gap-3">
              {form.cover_image_url && (
                <div className="relative rounded-lg overflow-hidden border border-slate-200">
                  <img src={form.cover_image_url.startsWith('/') ? `${API_URL}${form.cover_image_url}` : form.cover_image_url} alt={form.cover_image_alt} className="w-full h-48 object-cover" />
                  <button
                    onClick={() => updateField('cover_image_url', '')}
                    className="absolute top-2 right-2 p-1 bg-white rounded-full shadow"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
              <div className="flex gap-3">
                <input type="file" ref={fileInputRef} accept="image/*" className="hidden" onChange={handleImageUpload} />
                <Button variant="outline" onClick={() => fileInputRef.current?.click()} className="gap-2" data-testid="upload-cover-btn">
                  <Upload className="w-4 h-4" /> Upload Image
                </Button>
                <Input
                  value={form.cover_image_url}
                  onChange={(e) => updateField('cover_image_url', e.target.value)}
                  placeholder="Or paste image URL..."
                  className="flex-1"
                  data-testid="cover-url-input"
                />
              </div>
              <Input
                value={form.cover_image_alt}
                onChange={(e) => updateField('cover_image_alt', e.target.value)}
                placeholder="Alt text for SEO..."
                data-testid="cover-alt-input"
              />
            </div>
          </div>

          {/* Key Takeaways */}
          <div className="bg-white rounded-lg border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-3">
              <Label className="text-sm font-medium">Key Takeaways (max 5)</Label>
              {form.key_takeaways.length < 5 && (
                <Button variant="ghost" size="sm" onClick={addTakeaway} className="gap-1 text-xs" data-testid="add-takeaway-btn">
                  <Plus className="w-3 h-3" /> Add
                </Button>
              )}
            </div>
            <div className="space-y-2">
              {form.key_takeaways.map((kt, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <GripVertical className="w-4 h-4 text-slate-300 shrink-0" />
                  <Input
                    value={kt}
                    onChange={(e) => updateTakeaway(idx, e.target.value)}
                    placeholder={`Takeaway ${idx + 1}`}
                    data-testid={`takeaway-${idx}`}
                  />
                  <button onClick={() => removeTakeaway(idx)} className="p-1 hover:bg-red-50 rounded shrink-0">
                    <X className="w-4 h-4 text-red-400" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* FAQs */}
          <div className="bg-white rounded-lg border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-3">
              <Label className="text-sm font-medium">FAQs (max 6)</Label>
              {form.faqs.length < 6 && (
                <Button variant="ghost" size="sm" onClick={addFaq} className="gap-1 text-xs" data-testid="add-faq-btn">
                  <Plus className="w-3 h-3" /> Add FAQ
                </Button>
              )}
            </div>
            <div className="space-y-4">
              {form.faqs.map((faq, idx) => (
                <div key={idx} className="p-4 bg-slate-50 rounded-lg relative">
                  <button
                    onClick={() => removeFaq(idx)}
                    className="absolute top-2 right-2 p-1 hover:bg-red-50 rounded"
                  >
                    <X className="w-3 h-3 text-red-400" />
                  </button>
                  <div className="space-y-2">
                    <Input
                      value={faq.q}
                      onChange={(e) => updateFaq(idx, 'q', e.target.value)}
                      placeholder="Question"
                      className="font-medium"
                      data-testid={`faq-question-${idx}`}
                    />
                    <textarea
                      value={faq.a}
                      onChange={(e) => updateFaq(idx, 'a', e.target.value)}
                      placeholder="Answer"
                      rows={2}
                      className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      data-testid={`faq-answer-${idx}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* SEO Preview (collapsible) */}
          <div className="bg-white rounded-lg border border-slate-200">
            <button
              onClick={() => setSeoOpen(!seoOpen)}
              className="w-full flex items-center justify-between p-6"
              data-testid="seo-toggle"
            >
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-slate-500" />
                <span className="text-sm font-medium text-slate-900">SEO Preview</span>
              </div>
              {seoOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            {seoOpen && (
              <div className="px-6 pb-6 space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-sm">Meta Description</Label>
                    <span className={`text-xs ${(form.meta_description?.length || 0) > 160 ? 'text-red-500' : 'text-slate-400'}`}>
                      {form.meta_description?.length || 0}/160
                    </span>
                  </div>
                  <textarea
                    value={form.meta_description}
                    onChange={(e) => updateField('meta_description', e.target.value)}
                    placeholder="SEO meta description..."
                    maxLength={160}
                    rows={2}
                    className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    data-testid="meta-description-input"
                  />
                </div>
                {!isNew && (
                  <div>
                    <Label className="text-sm">Slug</Label>
                    <Input
                      value={form.slug}
                      onChange={(e) => updateField('slug', e.target.value)}
                      className="font-mono text-xs"
                      data-testid="post-slug-input"
                    />
                  </div>
                )}
                {/* Google search preview */}
                <div className="border border-slate-200 rounded-lg p-4 bg-slate-50">
                  <p className="text-xs text-slate-400 mb-1">Google Search Preview</p>
                  <p className="text-blue-700 text-base font-medium line-clamp-1">{form.title || 'Post Title'}</p>
                  <p className="text-green-700 text-xs">{`soulsanskar.com/blog/${form.slug || 'post-slug'}`}</p>
                  <p className="text-slate-600 text-sm mt-1 line-clamp-2">{form.meta_description || form.excerpt || 'Meta description will appear here...'}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Sidebar: Metadata */}
        <div className="space-y-6">
          {/* Category */}
          <div className="bg-white rounded-lg border border-slate-200 p-5">
            <Label className="text-sm font-medium mb-2 block">Category</Label>
            <select
              value={form.category}
              onChange={(e) => updateField('category', e.target.value)}
              className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white"
              data-testid="post-category-select"
            >
              {CATEGORIES.map(c => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>

          {/* Trimester Tags */}
          <div className="bg-white rounded-lg border border-slate-200 p-5">
            <Label className="text-sm font-medium mb-2 block">Trimester Tags</Label>
            <div className="flex gap-2">
              {TRIMESTER_OPTIONS.map(tag => (
                <button
                  key={tag}
                  onClick={() => toggleTrimesterTag(tag)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    form.trimester_tags.includes(tag)
                      ? 'bg-indigo-100 text-indigo-700 border-indigo-200'
                      : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'
                  }`}
                  data-testid={`trimester-tag-${tag}`}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>

          {/* Author */}
          <div className="bg-white rounded-lg border border-slate-200 p-5">
            <Label className="text-sm font-medium mb-2 block">Author</Label>
            <select
              value={form.author_slug}
              onChange={(e) => updateField('author_slug', e.target.value)}
              className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white"
              data-testid="post-author-select"
            >
              <option value="">Select author</option>
              {authors.map(a => (
                <option key={a.slug} value={a.slug}>{a.full_name}</option>
              ))}
            </select>
          </div>

          {/* Badge */}
          <div className="bg-white rounded-lg border border-slate-200 p-5">
            <Label className="text-sm font-medium mb-2 block">Badge</Label>
            <select
              value={form.badge || ''}
              onChange={(e) => updateField('badge', e.target.value)}
              className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white"
              data-testid="post-badge-select"
            >
              {BADGE_OPTIONS.map(b => (
                <option key={b.value} value={b.value}>{b.label}</option>
              ))}
            </select>
          </div>

          {/* Readers Count */}
          <div className="bg-white rounded-lg border border-slate-200 p-5">
            <Label className="text-sm font-medium mb-2 block">Readers Count</Label>
            <Input
              type="number"
              value={form.readers_count}
              onChange={(e) => updateField('readers_count', parseInt(e.target.value) || 0)}
              min={0}
              data-testid="post-readers-input"
            />
          </div>

          {/* Publish Date */}
          <div className="bg-white rounded-lg border border-slate-200 p-5">
            <Label className="text-sm font-medium mb-2 block">Publish Date</Label>
            <Input
              type="date"
              value={form.publish_date}
              onChange={(e) => updateField('publish_date', e.target.value)}
              data-testid="post-publish-date"
            />
          </div>

          {/* Featured Toggle */}
          {!isNew && (
            <div className="bg-white rounded-lg border border-slate-200 p-5">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">Featured (Hero Slot)</Label>
                <Switch
                  checked={form.is_featured}
                  onCheckedChange={async (checked) => {
                    if (checked) {
                      try {
                        await axios.patch(`${API_URL}/api/admin/blog/posts/${slug}/feature`);
                        updateField('is_featured', true);
                        toast.success('Set as featured');
                      } catch (err) {
                        toast.error('Failed to set featured');
                      }
                    }
                  }}
                  data-testid="post-featured-toggle"
                />
              </div>
              <p className="text-xs text-slate-400 mt-1">Only one post can be featured at a time</p>
            </div>
          )}

          {/* Next Reads */}
          <div className="bg-white rounded-lg border border-slate-200 p-5">
            <Label className="text-sm font-medium mb-2 block">
              Next Reads ({form.next_reads.length}/3)
            </Label>
            <div className="space-y-1.5 max-h-48 overflow-y-auto">
              {publishedSlugs.length === 0 ? (
                <p className="text-xs text-slate-400">No other published posts</p>
              ) : (
                publishedSlugs.map(s => (
                  <label key={s} className="flex items-center gap-2 text-xs cursor-pointer py-1">
                    <input
                      type="checkbox"
                      checked={form.next_reads.includes(s)}
                      onChange={() => toggleNextRead(s)}
                      disabled={!form.next_reads.includes(s) && form.next_reads.length >= 3}
                      className="rounded border-slate-300"
                    />
                    <span className="text-slate-700 truncate">{s}</span>
                  </label>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
